import { queryClient } from '@/query-client';
import ArrowRightOutlined from '@ant-design/icons/ArrowRightOutlined';
import HistoryOutlined from '@ant-design/icons/HistoryOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import PlayCircleOutlined from '@ant-design/icons/PlayCircleOutlined';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import message from 'antd/es/message';
import Progress from 'antd/es/progress';
import Select from 'antd/es/select';
import theme from 'antd/es/theme';
import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';

import type { AgentRun } from '@mai/types';

import { AGENT_RUNS_QUERY_KEY, useAgentRunsQuery } from '@queries/agent-runs';
import { TASKS_QUERY_KEY, useUpdateTaskDetailsMutation } from '@queries/tasks';
import { useWebsocketPubSub } from '@queries/websockets';

type AgentRunWithProgress = AgentRun & {
  progress: number;
};

const { Text, Paragraph, Title } = Typography;

// Global styles for dropdown that gets rendered in a portal outside our component
const RevisionDropdownStyles = createGlobalStyle`
  .revision-dropdown {
    // Let the dropdown use theme colors instead of forcing white
    &.ant-select-dropdown {
      background-color: ${(props) => props.theme.token.colorBgElevated} !important;
    }
    
    // Style for all menu items
    .ant-select-item {
      color: ${(props) => props.theme.token.colorText};
      
      &:hover {
        background-color: ${(props) => props.theme.token.colorBgTextHover} !important;
      }
      
      // Active/selected items
      &.ant-select-item-option-active,
      &.ant-select-item-option-selected {
        color: ${(props) => props.theme.token.colorPrimary};
        font-weight: 500;
        background-color: ${(props) => props.theme.token.colorPrimaryBg} !important;
      }
    }
    
    // Ensure text content inherits the right color
    .ant-select-item-option-content {
      color: inherit;
    }
  }
`;

// Clean, simple card without gradients
const OutputCard = styled(Card)<{ $bgColor: string }>`
  border-radius: 12px;
  background-color: ${(props) => props.$bgColor};
  border: none;

  .ant-card-body {
    padding: 24px;
  }

  // Global text styles for the card (including header)
  .ant-card-head-title,
  .ant-card-head-wrapper,
  .output-text-primary {
    color: ${(props) => props.theme.token.colorTextLightSolid};
  }

  .output-text-secondary {
    color: rgba(255, 255, 255, 0.85);
  }

  // Override Ant Design typography styles
  .ant-typography {
    color: ${(props) => props.theme.token.colorTextLightSolid};
  }

  code {
    background-color: rgba(255, 255, 255, 0.1);
  }

  // Style for links in the card
  a {
    color: ${(props) => props.theme.token.colorTextLightSolid};
    &:hover {
      opacity: 0.8;
    }
  }

  // Custom styling for Progress component to ensure visibility
  .ant-progress {
    .ant-progress-inner {
      background-color: rgba(255, 255, 255, 0.2);
    }

    .ant-progress-bg {
      background-color: rgba(255, 255, 255, 0.9);
    }
  }
`;

// Styled component for the revision selector to ensure proper contrast
const RevisionSelect = styled(Select)`
  // Main selector styling
  .ant-select-selector {
    background-color: rgba(0, 0, 0, 0.2) !important;
    border-color: rgba(255, 255, 255, 0.3) !important;
    transition: all 0.2s ease;

    &:hover {
      background-color: rgba(0, 0, 0, 0.3) !important;
      border-color: rgba(255, 255, 255, 0.5) !important;
    }
  }

  // Ensure text and icon are clearly visible
  .ant-select-selection-item {
    color: white !important;
    font-weight: 500;
  }

  .ant-select-arrow {
    color: white !important;
  }

  // Focus state
  &.ant-select-focused {
    .ant-select-selector {
      border-color: white !important;
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2) !important;
    }
  }
`;

// Custom styling for Markdown content when using expand/collapse
const StyledMarkdownWrapper = styled.div`
  p {
    color: rgba(255, 255, 255, 0.85);
  }

  code {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

// Type definitions
type OutputRevision =
  | {
      agentRunId: string;
      type: 'final_answer';
      text: string;
      createdAt: Date;
    }
  | {
      agentRunId: string;
      type: 'progress';
      createdAt: Date;
      estimatedTime: string;
      progress: number;
      latestThought?: string;
    };

// Helper function to calculate time remaining in a concise format
const getTimeRemaining = (
  createdAt: Date,
  maxActions: number,
  currentStep: number,
  subtaskAgentRuns?: AgentRunWithProgress[],
): string => {
  // Base time calculation for main task
  const minimumStepTimeMs = 60 * 1000;
  const averageStepTimeMs =
    (new Date().getTime() - createdAt.getTime()) / maxActions;
  const mainTaskTimeRemainingMs =
    Math.max(minimumStepTimeMs, averageStepTimeMs) * (maxActions - currentStep);

  // Add time from subtasks if they exist
  const subtaskTimeRemainingMs =
    subtaskAgentRuns?.reduce((acc, subtask) => {
      const subtaskMaxSteps = subtask.maxActions ?? 20;
      const subtaskCurrentStep = subtask.currentStep ?? 0;
      const subtaskAverageTimeMs =
        (new Date().getTime() - subtask.createdAt.getTime()) / subtaskMaxSteps;
      return (
        acc +
        Math.max(minimumStepTimeMs, subtaskAverageTimeMs) *
          (subtaskMaxSteps - subtaskCurrentStep)
      );
    }, 0) ?? 0;

  const totalTimeRemainingMs = mainTaskTimeRemainingMs + subtaskTimeRemainingMs;

  // Convert to closest unit
  let timeRemainingString: string;
  if (totalTimeRemainingMs > 1000 * 60 * 60 * 24) {
    timeRemainingString = `${Math.floor(totalTimeRemainingMs / (1000 * 60 * 60 * 24))}d`;
  } else if (totalTimeRemainingMs > 1000 * 60 * 60) {
    timeRemainingString = `${Math.floor(totalTimeRemainingMs / (1000 * 60 * 60))}h`;
  } else if (totalTimeRemainingMs > 1000 * 60) {
    timeRemainingString = `${Math.floor(totalTimeRemainingMs / (1000 * 60))}m`;
  } else {
    timeRemainingString = `${Math.floor(totalTimeRemainingMs / 1000)}s`;
  }
  return `at least ${timeRemainingString}`;
};

const SubtaskProgress = ({
  agentRuns,
}: {
  agentRuns: AgentRunWithProgress[];
}) => {
  const { token } = theme.useToken();

  if (agentRuns.length === 0)
    return <span className="output-text-secondary">No active subtasks</span>;

  return (
    <div>
      {agentRuns?.map((agentRun) => (
        <div
          style={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Progress
            type="circle"
            strokeColor="#e6f4ff"
            trailColor={token.colorFillSecondary}
            strokeWidth={20}
            size={14}
            percent={agentRun.progress * 100}
            status="active"
            showInfo={false}
          />
          <Tooltip title={agentRun.agentInputSummary}>
            <InfoCircleOutlined
              style={{ color: '#e6f4ff', fontSize: '14px' }}
            />
          </Tooltip>
          <span
            className="output-text-secondary"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            {agentRun.thoughts.at(-1)?.text.trim() || 'Thinking...'}
          </span>
        </div>
      ))}
    </div>
  );
};

const StartTaskButton = styled(Button)`
  margin-top: 16px;
  height: 40px;
  font-weight: 500;
  padding: 0 24px;

  // Ensure high contrast on dark background
  &.ant-btn-primary {
    background-color: white;
    color: ${(props) => props.theme.token.colorPrimary};
    border-color: white;

    &:hover {
      background-color: rgba(255, 255, 255, 0.9);
      border-color: rgba(255, 255, 255, 0.9);
    }
  }
`;

const TaskOutput: React.FC<{
  taskId: string;
  teamId: string;
  projectId: string;
}> = ({ taskId, teamId, projectId }) => {
  // Theme
  const { token } = theme.useToken();

  // State
  const [selectedRevisionIndex, setSelectedRevisionIndex] = useState<number>(0);

  // Fetch agent runs
  const { data: agentRuns } = useAgentRunsQuery(
    {
      taskIds: [taskId],
      agentTypes: ['task', 'general', 'search'],
    },
    !!taskId,
  );

  // Get mutation for updating task
  const updateTaskMutation = useUpdateTaskDetailsMutation(taskId);

  // Handle starting task
  const handleStartTask = async () => {
    try {
      await updateTaskMutation.mutateAsync({ shouldStart: true });
      void message.success('Task started successfully');
    } catch (error) {
      // Using message instead of console.error to avoid linter errors
      void message.error('Failed to start task');
    }
  };

  // Subscription hooks
  const { subscribe, unsubscribe } = useWebsocketPubSub();

  // Setup query invalidation hook
  useEffect(() => {
    const subscriptionIds = agentRuns?.map((agentRun) =>
      subscribe(
        {
          options: {
            resourceType: 'agent_run',
            resourceId: agentRun.agentRunId,
          },
          topic: 'resource_updated',
        },
        ({ type, data }) => {
          if (type !== 'resource_updated') return;
          if (data?.resourceType !== 'agent_run') return;
          if (data?.resourceId !== agentRun.agentRunId) return;
          void queryClient.invalidateQueries({
            queryKey: [AGENT_RUNS_QUERY_KEY],
          });
        },
      ),
    );

    return () => {
      subscriptionIds?.forEach(unsubscribe);
    };
    // NOTE: do not include subscribe/unsubscribe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentRuns, taskId, teamId, projectId]);

  // Setup query invalidation hook
  useEffect(() => {
    const subscriptionIds = agentRuns?.map(() =>
      subscribe(
        {
          options: {
            resourceType: 'task',
            resourceId: taskId,
          },
          topic: 'resource_updated',
        },
        ({ type, data }) => {
          if (type !== 'resource_updated') return;
          if (data?.resourceType !== 'task') return;
          if (data?.resourceId !== taskId) return;
          void queryClient.invalidateQueries({
            queryKey: [AGENT_RUNS_QUERY_KEY],
          });
          void queryClient.invalidateQueries({
            queryKey: [TASKS_QUERY_KEY],
          });
        },
      ),
    );

    return () => {
      subscriptionIds?.forEach(unsubscribe);
    };
    // NOTE: do not include subscribe/unsubscribe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentRuns, taskId, teamId, projectId]);

  // Process agent runs data
  const outputRevisions: OutputRevision[] = useMemo(() => {
    if (!agentRuns) return [];

    return agentRuns
      .filter((agentRun) => agentRun.agentType === 'task')
      .map((agentRun) => {
        if (agentRun.agentOutput && agentRun.agentOutputSummary) {
          return {
            agentRunId: agentRun.agentRunId,
            type: 'final_answer' as const,
            text: agentRun.agentOutputSummary,
            createdAt: agentRun.createdAt,
          };
        }

        const currentStep = agentRun.currentStep ?? 0;
        const maxActions = agentRun.maxActions ?? 20;

        return {
          agentRunId: agentRun.agentRunId,
          type: 'progress' as const,
          estimatedTime: getTimeRemaining(
            agentRun.createdAt,
            maxActions,
            currentStep,
            agentRuns
              ?.filter(
                (agentRun) =>
                  agentRun.agentType === 'general' && !agentRun.agentOutput,
              )
              .map((agentRun) => ({
                ...agentRun,
                progress:
                  (agentRun.currentStep ?? 0) / (agentRun.maxActions ?? 20),
              }))
              .sort((a, b) => b.progress - a.progress),
          ),
          progress: (currentStep / maxActions) * 100,
          latestThought: agentRun.thoughts.at(-1)?.text,
          createdAt: agentRun.createdAt,
        };
      })
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }, [agentRuns]);

  const subtaskAgentRuns = useMemo(() => {
    return agentRuns
      ?.filter(
        (agentRun) =>
          (agentRun.agentType === 'general' ||
            agentRun.agentType === 'search') &&
          !agentRun.agentOutput,
      )
      .map((agentRun) => ({
        ...agentRun,
        progress: (agentRun.currentStep ?? 0) / (agentRun.maxActions ?? 20),
      }))
      .sort((a, b) => b.progress - a.progress);
  }, [agentRuns]);

  // Current revision and agent run ID
  const currentRevision = useMemo(
    () => outputRevisions[selectedRevisionIndex] || null,
    [outputRevisions, selectedRevisionIndex],
  );

  const currentAgentRunId = useMemo(
    () => outputRevisions?.[selectedRevisionIndex]?.agentRunId,
    [outputRevisions, selectedRevisionIndex],
  );

  // Revision selection options
  const revisionOptions = useMemo(
    () =>
      outputRevisions.map((revision, index) => ({
        value: index,
        label: (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <HistoryOutlined style={{ marginRight: 8 }} />
            <span>{new Date(revision.createdAt).toLocaleString()}</span>
          </div>
        ),
      })),
    [outputRevisions],
  );

  const handleRevisionChange = (value: unknown) => {
    setSelectedRevisionIndex(Number(value));
  };

  // Card title with revisions selector
  const cardTitle = revisionOptions.length > 0 && (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <Text className="output-text-secondary" style={{ fontSize: 14 }}>
        Revision:
      </Text>
      <RevisionSelect
        options={revisionOptions}
        value={selectedRevisionIndex}
        onChange={handleRevisionChange}
        style={{ minWidth: 250 }}
        dropdownMatchSelectWidth={false}
        popupClassName="revision-dropdown"
      />
    </div>
  );

  const hasAgentRuns = useMemo(() => {
    return agentRuns && agentRuns.length > 0;
  }, [agentRuns]);

  return (
    <>
      <RevisionDropdownStyles />
      <OutputCard
        $bgColor={token.colorPrimary}
        title={cardTitle}
        headStyle={{
          backgroundColor: 'transparent',
          borderBottom: 'none',
          padding: '16px 24px 0',
        }}
      >
        {!hasAgentRuns ? (
          <div
            style={{ width: '100%', textAlign: 'center', padding: '24px 0' }}
          >
            <Title
              level={4}
              className="output-text-primary"
              style={{ marginBottom: 8, marginTop: 0 }}
            >
              Task Not Started
            </Title>

            <Paragraph
              className="output-text-secondary"
              style={{
                maxWidth: 600,
                marginBottom: 16,
                margin: '0 auto',
              }}
            >
              This task hasn't been started yet. Click the button below to start
              processing.
            </Paragraph>

            <StartTaskButton
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={handleStartTask}
              loading={updateTaskMutation.isPending}
            >
              Start Task
            </StartTaskButton>
          </div>
        ) : currentRevision?.type === 'final_answer' ? (
          <>
            <StyledMarkdownWrapper>
              <Paragraph
                className="output-text-secondary"
                style={{ marginBottom: 16 }}
                ellipsis={{
                  rows: 10,
                }}
              >
                {currentRevision.text}
              </Paragraph>
            </StyledMarkdownWrapper>

            {currentAgentRunId && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Link
                  to={`/team/${teamId}/project/${projectId}/tasks/${taskId}/agent-runs/${currentAgentRunId}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                    fontWeight: 500,
                  }}
                >
                  View full output <ArrowRightOutlined />
                </Link>
              </div>
            )}
          </>
        ) : (
          <div style={{ width: '100%', textAlign: 'center' }}>
            <Title
              level={4}
              className="output-text-primary"
              style={{ marginBottom: 8, marginTop: 0 }}
            >
              Processing Your Task
            </Title>

            <Paragraph
              className="output-text-secondary"
              style={{
                maxWidth: 600,
                marginBottom: 16,
                margin: '0 auto',
              }}
            >
              Our AI is working on your task. This might take a few minutes
              depending on complexity.
            </Paragraph>

            <div
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                margin: '16px auto',
                maxWidth: 1000,
              }}
            >
              <div style={{ flex: 1, width: '100%' }}>
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    gap: 8,
                  }}
                >
                  <span className="output-text-primary">Overall Progress</span>
                  <span
                    className="output-text-secondary"
                    style={{
                      fontSize: 12,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {` (${currentRevision?.estimatedTime ?? ''} remaining)`}
                  </span>
                </div>
                <Progress
                  percent={currentRevision?.progress ?? 10}
                  status="active"
                  showInfo={false}
                  strokeColor="white"
                  trailColor="rgba(255, 255, 255, 0.2)"
                  strokeWidth={10}
                />
                <div
                  className="output-text-secondary"
                  style={{
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    marginTop: 8,
                  }}
                >
                  <LoadingOutlined />
                  <span>
                    {currentRevision?.latestThought ?? 'Processing...'}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginTop: 8,
                  }}
                >
                  <span className="output-text-primary">Subtask Progress</span>
                </div>
                <SubtaskProgress agentRuns={subtaskAgentRuns ?? []} />
              </div>
            </div>
          </div>
        )}
      </OutputCard>
    </>
  );
};

export default TaskOutput;
